import { useTranslation } from "@opendash/core";
import { Button, Divider, Drawer, Form, Space } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useRef, useState } from "react";
import { Item } from "../../../js/parsegenerated";
import useSaveItem from "../hooks/useSaveItem";
import FormData from "../types/FormData";
import Category from "./form/items/Category";
import InternalNr from "./form/items/InternalNr";
import Meta from "./form/items/Meta";
import OrderNr from "./form/items/OrderNr";
import Price from "./form/items/Price";
import Sources from "./form/items/Sources";
import Stock from "./form/items/Stock";
import StorageLocation from "./form/items/StorageLocation";
import Supplier from "./form/items/Supplier";

export default observer(
  (props: {
    open: boolean;
    onCancel: Function;
    onOk: Function;
    item?: Item;
  }) => {
    const t = useTranslation();
    const [form] = Form.useForm<FormData>();
    const { saveItem, saving } = useSaveItem();
    const [allItems, setAllItems] = useState<Item[]>([]);
    const formRef = useRef(undefined); // needed to check if form is linked

    useEffect(() => {
      async function getItems() {
        const items = await new Parse.Query(Item).find();
        setAllItems(items);
      }
      getItems();
    }, [saving]);

    // Setting form values depending on selected item
    // clears all when a new material should be created instead
    useEffect(() => {
      if (formRef.current)
        if (!props.item)
          form?.setFieldsValue({
            category: undefined,
            description: undefined,
            internalNumber: undefined,
            orderNumber: undefined,
            price: undefined,
            stock: undefined,
            stockAlarmAt: undefined,
            stockUnit: undefined,
            storageLocation: undefined,
            supplier: undefined,
            title: undefined,
            source: undefined,
          });
        else
          props.item?.sources
            .query()
            .find()
            .then((sources) => {
              form?.setFieldsValue({
                category: props.item?.category,
                description: props.item?.description,
                internalNumber: props.item?.internalNumber,
                orderNumber: props.item?.orderNumber,
                price: props.item?.price,
                stock: props.item?.stock,
                stockAlarmAt: props.item?.stockAlarmAt,
                stockUnit: props.item?.stockUnit,
                storageLocation: props.item?.storageLocation,
                supplier: props.item?.supplier,
                title: props.item?.name,
                source: sources.map((source) => source.id),
              });
            });
    }, [props.item, formRef.current]);

    const onClose = () => {
      form.resetFields();
      props.onCancel();
    };

    const onSave = async () => {
      const values = await form.validateFields();
      saveItem(
        values,
        () => {
          props.onOk();
          form.resetFields();
        },
        props.item
      );
    };

    return (
      <Drawer
        title={t("maintenance:items.create.headline")}
        placement="bottom"
        height={"70%"}
        onClose={onClose}
        open={props.open}
        extra={
          <Space>
            <Button onClick={onClose}>
              {t("maintenance:global.button.cancel_label")}
            </Button>
            <Button type="primary" onClick={onSave} loading={saving}>
              {t("maintenance:global.button.save_label")}
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={form} ref={formRef}>
          <Meta editItem={props.item} />
          <Divider />
          <Sources editItem={props.item} />
          <Divider />
          <Category items={allItems} editItem={props.item} />
          <Divider />
          <Price editItem={props.item} />
          <Divider />
          <Stock items={allItems} editItem={props.item} />
          <Divider />
          <InternalNr editItem={props.item} />
          <Divider />
          <StorageLocation items={allItems} editItem={props.item} />
          <Divider />
          <Supplier items={allItems} editItem={props.item} />
          <OrderNr editItem={props.item} />
          <Divider />
        </Form>
      </Drawer>
    );
  }
);
